import { ref, onUnmounted } from 'vue';
import { isCapacitor } from '../config/runtime';
import { apiFetch } from '../services/api';
import { cloudFetch } from '../services/cloudApi';
import { getOrCreatePushDeviceKey } from '../services/pushNotifications';
import { readPresenceConsent } from '../services/presenceConsent';
import { sendCloudPresence, type CloudPresenceOutcome, type LocationFix } from '../services/cloudPresence';
import {
  observeTrackerError,
  observeTrackerFix,
  observeTrackerStart,
  observeTrackerStop,
  recordLocationReport,
  type LocationReportResult,
} from '../services/presenceDiagnostics';
import { getOrCreateDeviceFingerprint } from '../utils/deviceFingerprint';
import { useAuthStore } from '../stores/auth';
import { useSocketStore, type SocketHandler } from '../stores/socket';

/**
 * Composable for native GPS location tracking on Capacitor (mobile) devices.
 * Falls back gracefully to no-op on non-native platforms.
 *
 * Features:
 * - Requests permission and watches GPS position
 * - Posts location to the backend at a configurable interval
 * - Reports the same fix to the CLOUD when the account is signed in and the
 *   user opted in (cloud-defined areas are evaluated there, card A1)
 * - Responds to "find my device" socket events with immediate location + alert
 * - Respects user's consent flag (the hub enforces it on its own report; the
 *   cloud report is gated on the same opt-in)
 * - Starts once per page session, and is safe to ask again on every connect
 *   (the session watcher in `App.vue`; card `t_88c3d858`)
 */
export function useLocationTracking() {
  const tracking = ref(false);
  const lastPosition = ref<{ latitude: number; longitude: number; accuracy: number } | null>(null);
  const error = ref<string | null>(null);

  let watchId: string | null = null;
  let postInterval: ReturnType<typeof setInterval> | null = null;
  let findDeviceHandler: SocketHandler | null = null;
  /** One start per page session: an ask while one is running is a no-op. */
  let startInFlight = false;
  /** The Capacitor geolocation plugin — loaded by `startTracking()` on native only. */
  let Geolocation: typeof import('@capacitor/geolocation').Geolocation | null = null;

  const TRACKING_INTERVAL_MS = 60_000; // 60s while active

  /**
   * Start GPS tracking. Safe to ask again on every connect: the first live
   * watch wins (Capacitor only; card `t_88c3d858`).
   */
  async function startTracking(): Promise<void> {
    if (!isCapacitor()) return;

    // One start per page session (card `t_88c3d858`): the session watcher in
    // `App.vue` asks on every connect and reconnect, and a second ask must be
    // a no-op while a start is running or once the watch is live. `tracking`
    // alone cannot answer the first half — it flips only after `watchPosition`
    // resolved, so two asks in the same tick would both pass it and two
    // watches would be created.
    if (startInFlight || tracking.value) return;
    startInFlight = true;

    // The id this install reports under. Created when absent: on a
    // cloud-relay-only install the local socket path that used to be its only
    // writer never runs, and the old `'unknown'` bail left the tracker silent
    // (`utils/deviceFingerprint.ts`).
    const deviceId = getOrCreateDeviceFingerprint();

    try {
      // Dynamic import — only loaded on native
      const geo = await import('@capacitor/geolocation');
      const geolocation = geo.Geolocation;
      Geolocation = geolocation;

      // Request permission
      const permResult = await geolocation.requestPermissions();
      if (permResult.location === 'denied') {
        const denied = 'Location permission denied';
        error.value = denied;
        observeTrackerError(denied);
        return;
      }

      // Start watching
      watchId = await geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 60000 },
        (pos, err) => {
          if (err) {
            const message = err.message || 'Location error';
            error.value = message;
            observeTrackerError(message);
            return;
          }
          if (pos) {
            lastPosition.value = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            };
            // The diagnostic's "last fix seen" (card `t_9ec98b5c`) — when the
            // tracker last heard from the OS, never where.
            observeTrackerFix(Date.now());
          }
        },
      );

      tracking.value = true;
      // This session now has a live watch — the presence page's diagnostic
      // block reads it from the observation module (card `t_9ec98b5c`).
      observeTrackerStart();

      // The opt-in the CLOUD report is gated on (plan Q4). Fire-and-forget —
      // the answer gates the first report and every report re-reads it anyway,
      // so a hub that is not answering yet must not hold the tracker up.
      void readPresenceConsent();

      // Post location to backend periodically
      postInterval = setInterval(() => {
        if (lastPosition.value) {
          postLocation(deviceId, lastPosition.value);
        }
      }, TRACKING_INTERVAL_MS);

      // Send initial position
      const initialPos = await geolocation.getCurrentPosition({ enableHighAccuracy: true });
      if (initialPos) {
        lastPosition.value = {
          latitude: initialPos.coords.latitude,
          longitude: initialPos.coords.longitude,
          accuracy: initialPos.coords.accuracy,
        };
        postLocation(deviceId, lastPosition.value);
      }

      // Listen for "find my device" events
      const socketStore = useSocketStore();
      findDeviceHandler = (data: { deviceId: string }) => {
        if (data.deviceId === deviceId) {
          handleFindDeviceRequest(deviceId);
        }
      };
      socketStore.on('device:find-request', findDeviceHandler);
    } catch (__caught: unknown) {
      const e = __caught as Error;
      const message = e.message || 'Failed to start location tracking';
      error.value = message;
      observeTrackerError(message);
      console.error('[LocationTracking] Error:', e);
    } finally {
      startInFlight = false;
    }
  }

  /**
   * Stop GPS tracking and clean up.
   */
  async function stopTracking(): Promise<void> {
    const geolocation = Geolocation;
    if (watchId && geolocation) {
      await geolocation.clearWatch({ id: watchId });
      watchId = null;
    }

    if (postInterval) {
      clearInterval(postInterval);
      postInterval = null;
    }

    if (findDeviceHandler) {
      const socketStore = useSocketStore();
      socketStore.off('device:find-request', findDeviceHandler);
      findDeviceHandler = null;
    }

    tracking.value = false;
    // The watch is gone — the diagnostic block must stop claiming it runs
    // (card `t_9ec98b5c`).
    observeTrackerStop();
  }

  /**
   * Post current position to the backend.
   *
   * Both reports go out from here: the hub's (unchanged — it feeds the hub's
   * own local zones) and the cloud's (card A1 — cloud areas are evaluated
   * there). They are independent: each has its own catch, so one failing (or
   * being gated off) never affects the other.
   *
   * Both results are recorded for the presence page's diagnostic block (card
   * `t_9ec98b5c`) — two result words and a time, never the fix: that is what
   * makes "is my phone reporting?" a glance instead of an hour of log-reading.
   */
  async function postLocation(
    deviceId: string,
    position: { latitude: number; longitude: number; accuracy: number },
  ): Promise<void> {
    let hubResult: LocationReportResult = 'sent';
    try {
      await apiFetch('/presence/location', {
        method: 'POST',
        body: JSON.stringify({
          deviceId,
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        }),
      });
    } catch (e) {
      hubResult = 'failed';
      console.warn('[LocationTracking] Failed to post location:', e);
    }

    const cloudResult = await reportCloudLocation(position);
    recordLocationReport({ at: Date.now(), hub: hubResult, cloud: cloudResult });
  }

  /**
   * Report one fix to the CLOUD's presence evaluator (card A1).
   *
   * Gated on the signed-in account and on the user's recorded opt-in, both read
   * at call time; `sendCloudPresence` owns the request and catches its own
   * failures (one log line), so nothing here can break the hub report. The
   * outcome is answered back — the caller records it (card `t_9ec98b5c`).
   */
  async function reportCloudLocation(fix: LocationFix): Promise<CloudPresenceOutcome> {
    return sendCloudPresence(fix, {
      isCloudAuthenticated: () => useAuthStore().isCloudAuthenticated,
      hasConsent: readPresenceConsent,
      deviceKey: getOrCreatePushDeviceKey,
      post: cloudFetch,
    });
  }

  /**
   * Handle a "find my device" request — immediately fetch GPS and play an alert.
   */
  async function handleFindDeviceRequest(deviceId: string): Promise<void> {
    try {
      // Get fresh position
      const geolocation = Geolocation;
      if (geolocation) {
        const pos = await geolocation.getCurrentPosition({ enableHighAccuracy: true });
        if (pos) {
          lastPosition.value = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          await postLocation(deviceId, lastPosition.value);
        }
      }

      // Play alert sound
      playFindAlert();
    } catch (e) {
      console.error('[LocationTracking] Error handling find-device:', e);
    }
  }

  /**
   * Play an audible alert when the device is being found.
   */
  function playFindAlert(): void {
    try {
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.value = 880; // A5
      gainNode.gain.value = 0.5;

      oscillator.start();
      // Beep pattern: on 200ms, off 200ms, on 200ms, off 200ms, on 200ms
      setTimeout(() => {
        gainNode.gain.value = 0;
      }, 200);
      setTimeout(() => {
        gainNode.gain.value = 0.5;
      }, 400);
      setTimeout(() => {
        gainNode.gain.value = 0;
      }, 600);
      setTimeout(() => {
        gainNode.gain.value = 0.5;
      }, 800);
      setTimeout(() => {
        oscillator.stop();
        ctx.close();
      }, 1000);
    } catch {
      // AudioContext may not be available
    }
  }

  onUnmounted(() => {
    stopTracking();
  });

  return {
    tracking,
    lastPosition,
    error,
    startTracking,
    stopTracking,
  };
}
